import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, setAuthToken } from "@/lib/api";

// Operator identity is resolved entirely from /api/v2/operator/me — the server
// derives the operator from the trusted carrier-side request context, so a
// successful response IS the proof of operator-hood. We no longer read Auth0
// custom claims (roles / internalUserId) from the access token.

export interface OperatorIdentity {
  id: string;
  carrierId?: string | null;
  dispatchCenterId?: string | null;
  dispatchCenter?: { id: string; name: string; timeZone: string } | null;
  carrierUserId?: string | null;
  carrierUser?: {
    id: string;
    name: string | null;
    email: string;
    phone?: string | null;
    active?: boolean;
  };
}

export function unwrapCurrentOperator(
  payload: unknown
): OperatorIdentity | null {
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    const data = obj.data as Record<string, unknown> | undefined;
    const operator = data?.operator ?? obj;

    if (operator && typeof operator === "object") {
      const candidate = operator as Record<string, unknown>;
      if (typeof candidate.id === "string") {
        return candidate as unknown as OperatorIdentity;
      }
    }
  }

  return null;
}

async function fetchCurrentOperator() {
  const response = await api.get("/api/v2/operator/me");
  const operator = unwrapCurrentOperator(response.data);

  if (!operator) {
    throw new Error("Current operator response did not include an operator");
  }

  return operator;
}

interface SessionContextType {
  token: string | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  isOperator: boolean;
  operatorId: string | null;
  operator: OperatorIdentity | null;
  login: () => void;
  logout: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const {
    isLoading: isAuth0Loading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();

  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  const logout = useCallback(
    () => auth0Logout({ logoutParams: { returnTo: window.location.origin } }),
    [auth0Logout]
  );

  useEffect(() => {
    if (isAuth0Loading) return;
    if (!isAuthenticated) {
      setIsLoading(false);
      setToken(null);
      setAuthToken(null);
      return;
    }
    const run = async () => {
      try {
        const t = await getAccessTokenSilently();
        setToken(t);
        setAuthToken(t);
      } catch (error) {
        console.error("Error getting token:", error);
        void logout();
      } finally {
        setIsLoading(false);
      }
    };
    void run();
  }, [isAuthenticated, isAuth0Loading, getAccessTokenSilently, logout]);

  // Once we have a token, ask the server who this operator is. Operator-hood is
  // "did /operator/me return an operator?" — a signed-in non-operator gets a
  // 403/404, the query fails, `operator` stays undefined, and isOperator is
  // false (ProtectedRoute then routes them to /no-access as before).
  const { data: operator, isLoading: isOperatorLoading } = useQuery({
    queryKey: ["operator", "me", token],
    queryFn: fetchCurrentOperator,
    enabled: !!token,
    retry: false,
  });

  const operatorId = operator?.id ?? null;
  const isOperator = Boolean(operator);

  const value = useMemo<SessionContextType>(
    () => ({
      token,
      isLoading:
        isAuth0Loading || isLoading || Boolean(token && isOperatorLoading),
      isLoggedIn: isAuthenticated && !!token,
      isOperator,
      operatorId,
      operator: operator ?? null,
      login: () => void loginWithRedirect(),
      logout,
    }),
    [
      token,
      isAuth0Loading,
      isLoading,
      isAuthenticated,
      isOperator,
      operatorId,
      operator,
      isOperatorLoading,
      loginWithRedirect,
      logout,
    ]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (ctx === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return ctx;
}
